import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ForbiddenPage from '../components/server_responses/403Forbidden';
import NotFoundPage from '../components/server_responses/404NotFoundPage';

/**
 * @typedef {Object} FetchOptions
 * @property {'GET' | 'POST' | 'PUT' | 'DELETE'} [method]
 * @property {any} [body]
 * @property {boolean} [isBlob] - Indicates if the response should be treated as a Blob
 */

/**
 * Custom hook for making API requests
 * @returns {{
 *   fetchData: function,
 *   loading: boolean,
 *   error: Error|null,
 *   serverError: React.ReactNode|null
 * }}
 */
const useFetch = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [serverError, setServerError] = useState(null);
  const navigate = useNavigate();

  /**
   * @template T
   * @param {string} url
   * @param {FetchOptions} [options={}]
   * @returns {Promise<T|null>}
   */
  const fetchData = useCallback(async (url, options = {}) => {
    if (!options.isBlob) { // Only set loading if not a Blob request
      setLoading(true);
    }
    setError(null);
    setServerError(null);

    const jwtToken = localStorage.getItem('jwtToken');
    const baseUrl = process.env.REACT_APP_API_BASE_URL;

    try {
      const method = options.method || 'GET';
      const headers = {
        Authorization: jwtToken ? `Bearer ${jwtToken}` : '',
      };

      if (method !== 'GET') {
        headers['Content-Type'] = 'application/json';
      }

      const fetchOptions = {
        method,
        headers,
      };

      // Only add body for non-GET requests
      if (method !== 'GET' && options.body) {
        if (options.body instanceof FormData) {
          delete headers['Content-Type']; // Remove Content-Type for FormData
          fetchOptions.body = options.body; // Directly use the FormData object
        } else {
          fetchOptions.body = JSON.stringify(options.body); // Stringify for JSON
        }
      }

      console.log(`[useFetch] Requesting: ${method} ${baseUrl}${url}`);
      console.log('[useFetch] Headers:', { ...headers, Authorization: headers.Authorization ? 'Bearer [HIDDEN]' : 'None' });
      const response = await fetch(`${baseUrl}${url}`, fetchOptions);
      console.log(`[useFetch] Response status: ${response.status} ${response.statusText}`);

      if (response.status === 401) {
        // If we haven't retried yet, attempt to refresh the token
        if (!options.retry) {
          try {
            // Call your backend's refresh endpoint
            const refreshResponse = await fetch(`${baseUrl}/api/auth/refresh`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${jwtToken}`,
              },
            });

            if (refreshResponse.ok) {
              const data = await refreshResponse.json();
              localStorage.setItem('jwtToken', data.token);
              // Retry the original request with the new token and _retry flag
              return fetchData(url, { ...options, retry: true });
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
          }
        }

        // If refresh failed or we already retried, force logout
        localStorage.removeItem('jwtToken');
        navigate('/login');
        return null;
      }

      if (response.status === 403) {
        setServerError(<ForbiddenPage />);
        return null;
      }

      if (response.status === 404) {
        setServerError(<NotFoundPage />);
        return null;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'No error details from server.' }));
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorData.error || errorData.message}`);
      }

      // Check if the response should be treated as a Blob
      if (options.isBlob) {
        console.log('[useFetch] Processing response as Blob');
        const blob = await response.blob();
        console.log('[useFetch] Blob created. Size:', blob.size);
        return blob;
      }

      try {
        const data = await response.json();
        return data;
      } catch (parseError) {
        console.error('[useFetch] JSON Parse Error:', parseError);
        throw new Error('Server returned invalid JSON (likely HTML/404).');
      }
    } catch (err) {
      console.error('[useFetch] Fetch error:', err);
      setError(err instanceof Error ? err : new Error('An unknown error occurred'));
      return null;
    } finally {
      if (!options.isBlob) { // Only reset loading if not a Blob request
        setLoading(false);
      }
    }
  }, [navigate]);

  return {
    fetchData, loading, error, serverError,
  };
};

export default useFetch;
